import React from 'react';
import { DragIndicator as DragHandleIcon } from '@mui/icons-material';
import { T, fontBody, fontMono, nodeColor } from '../tokens';
import { buildCanvasDragPayload, setDragPayload } from '../utils/flowUtils';

export default function FlowNode({
  node,
  index,
  onClick,
  selected,
  dragging,
  dropPosition,
  onDragStart,
  onDragEnd,
  onNodeDragOver,
  onNodeDrop,
}) {
  const Icon = node.icon;
  const { bg, fg } = nodeColor(node.kind);
  const isTrigger = node.kind === 'trigger';
  const showTopLine = dropPosition === index;
  const showBottomLine = dropPosition === index + 1;

  const handleDragStart = (e) => {
    e.stopPropagation();
    setDragPayload(e, buildCanvasDragPayload(index));
    if (e.dataTransfer.setDragImage) {
      const ghost = e.currentTarget.closest('[data-flow-node]');
      if (ghost) e.dataTransfer.setDragImage(ghost, 130, 28);
    }
    onDragStart(e, index);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const insertIndex = e.clientY < rect.top + rect.height / 2 ? index : index + 1;
    onNodeDragOver(insertIndex);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const insertIndex = e.clientY < rect.top + rect.height / 2 ? index : index + 1;
    onNodeDrop(insertIndex, e);
  };

  return (
    <div
      data-flow-node
      draggable
      onDragStart={handleDragStart}
      onDragEnd={onDragEnd}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      style={{ width: 260, position: 'relative', cursor: 'grab' }}
    >
      {showTopLine && (
        <div style={{
          position: 'absolute', top: -6, left: 0, right: 0, height: 4, borderRadius: 999, background: T.blue, zIndex: 2,
        }}
        />
      )}
      <div
        style={{
          display: 'flex', alignItems: 'stretch', gap: 0,
          opacity: dragging ? 0.5 : 1,
          border: `1.5px solid ${selected ? fg : T.line}`,
          borderRadius: 12,
          background: T.card,
          boxShadow: selected ? `0 0 0 3px ${bg}` : 'none',
          overflow: 'hidden',
        }}
        className="sma-card-hover"
      >
        <div
          title="Drag to reorder"
          style={{
            width: 28, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: T.slateLight,
            background: T.page, borderRight: `1px solid ${T.lineSoft}`,
            pointerEvents: 'none',
          }}
        >
          <DragHandleIcon sx={{ fontSize: 16 }} />
        </div>
        <div
          role="button"
          tabIndex={0}
          onClick={() => onClick(node)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(node); }}
          style={{
            flex: 1, display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
            textAlign: 'left', cursor: 'pointer', minWidth: 0,
          }}
        >
          <div style={{
            width: 32, height: 32, borderRadius: 9, flexShrink: 0, background: bg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          >
            <Icon sx={{ fontSize: 15, color: fg }} />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{
              fontFamily: fontBody, fontSize: 10.5, fontWeight: 600, color: fg,
              textTransform: 'uppercase', letterSpacing: '0.04em',
            }}
            >
              {node.kind}
              {isTrigger && index === 0 ? ' · Start' : ''}
            </div>
            <div style={{
              fontFamily: fontBody, fontSize: 13.5, fontWeight: 600, color: T.ink,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}
            >
              {node.label}
            </div>
            {node.detail && (
              <div style={{
                fontFamily: fontMono, fontSize: 11, color: T.slateLight, marginTop: 1,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}
              >
                {node.detail}
              </div>
            )}
          </div>
        </div>
      </div>
      {showBottomLine && (
        <div style={{
          position: 'absolute', bottom: -6, left: 0, right: 0, height: 4, borderRadius: 999, background: T.blue, zIndex: 2,
        }}
        />
      )}
    </div>
  );
}
